import React, { useState, useEffect } from 'react';
import { FaCar, FaGasPump, FaTachometerAlt } from 'react-icons/fa';
import { TbSteeringWheel } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import Skeleton from '../ui/Skeleton';

function Listing() {
    const [listing, setListing] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchListing = async () => {
            try {
                const response = await fetch('/api/listing');
                if (!response.ok) {
                    console.error('API error:', response.status);
                    setListing([]);
                    setLoading(false);
                    return;
                }
                let data = await response.json();
                // Ensure data is an array
                if (!Array.isArray(data)) {
                    console.error('API returned non-array:', data);
                    data = [];
                }

                // Sort by date from new to old
                data.sort((a, b) => new Date(b.date) - new Date(a.date));

                data = data.filter(listing => listing.visibility === "Active");


                setListing(data.slice(0, 4)); // Get the latest 4 listings
            } catch (error) {
                // Error handled silently
            } finally {
                setLoading(false); // Set loading to false once the data is fetched
            }
        };

        fetchListing();
    }, []);

    const renderSkeleton = () => (
        <div className="relative shadow-md rounded-lg overflow-hidden p-4">
            <Skeleton className="w-full h-48 mb-2 rounded-md" />
            <Skeleton className="h-5 w-3/4 mb-2" />
            <Skeleton className="h-5 w-1/2 mb-2" />
            <Skeleton className="h-5 w-full mb-1" />
            <Skeleton className="h-5 w-full" />
        </div>
    );

    return (
        <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {loading ? (
                    // Display skeletons while loading
                    <>
                        {Array(4).fill(null).map((_, index) => (
                            <div key={index}>{renderSkeleton()}</div>
                        ))}
                    </>
                ) : (
                    listing.map((item) => (
                        <div key={item.id} className="relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                            <div className="relative">
                                {/* Image Section */}
                                <div className="relative overflow-hidden bg-gray-100">
                                    <Link to={`/cars/${item._id}`}>
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            className="w-full h-56 object-cover transition-transform duration-500 ease-in-out hover:scale-110"
                                        />
                                    </Link>
                                    {/* Condition Badge */}
                                    {item.itemCondition === "Used" && (
                                        <span className='absolute top-3 left-3 bg-[#DC2626] text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg uppercase tracking-wide'>
                                            {item.itemCondition}
                                        </span>
                                    )}
                                    {/* Price Badge - Red Oval */}
                                    <div className="absolute top-3 right-3 bg-[#DC2626] px-4 py-2 rounded-full shadow-lg">
                                        <p className='text-base font-bold text-white whitespace-nowrap drop-shadow-sm'>${item.price}</p>
                                    </div>
                                </div>
                                
                                {/* Content Section */}
                                <div className="p-5">
                                    {/* Title */}
                                    <Link to={`/cars/${item._id}`}>
                                        <h2 className="font-display text-lg font-semibold text-gray-900 mb-4 hover:text-[#DC2626] transition-colors line-clamp-2 min-h-[3.5rem]">
                                            {item.title}
                                        </h2>
                                    </Link>
                                    
                                    {/* Specifications Grid */}
                                    <div className='grid grid-cols-2 gap-3 pt-2 border-t border-gray-100'>
                                        <div className='flex items-center gap-2'>
                                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                                                <FaGasPump className="text-[#DC2626] text-sm" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Fuel</p>
                                                <p className="text-sm text-gray-900 font-semibold truncate">{item.fuelType}</p>
                                            </div>
                                        </div>
                                        
                                        <div className='flex items-center gap-2'>
                                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                                                <FaCar className="text-[#DC2626] text-sm" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Body</p>
                                                <p className="text-sm text-gray-900 font-semibold truncate">{item.bodyType}</p>
                                            </div>
                                        </div>
                                        
                                        <div className='flex items-center gap-2'>
                                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                                                <FaTachometerAlt className="text-[#DC2626] text-sm" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Mileage</p>
                                                <p className="text-sm text-gray-900 font-semibold truncate">{item.mileage} {item.mileageUnit}</p>
                                            </div>
                                        </div>
                                        
                                        <div className='flex items-center gap-2'>
                                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                                                <TbSteeringWheel className="text-[#DC2626] text-base" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Transmission</p>
                                                <p className="text-sm text-gray-900 font-semibold truncate">{item.vehicleTransmission}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

export default Listing;
